import { Badge, Button, Progress } from "@bcc/components/ui";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { XIcon } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TourStep, TourStepListItem } from "../tour.types";
import { setTourTestId } from "../tour.utils";
import type { TourTextParams } from "../use-tour-text-params";
import type { RectLike, SizeLike } from "./position-step-card";
import { positionStepCard } from "./position-step-card";

export interface TourStepCardProps {
  readonly step: TourStep;
  readonly stepIndex: number;
  readonly totalSteps: number;
  readonly targetRect: RectLike;
  /** True when the step's emptyState.detectTarget matched nothing — show the info body instead */
  readonly isTargetEmpty?: boolean;
  /** Interpolation values for the step copy, e.g. `{{platform}}` (see useTourTextParams) */
  readonly textParams?: TourTextParams;
  readonly onNext: () => void;
  readonly onPrev: () => void;
  readonly onSkip: () => void;
}

const FALLBACK_CARD_SIZE: SizeLike = { width: 384, height: 280 };

/** Label/description rows under the step body (e.g. what each status chip means) */
function TourStepList({ items, textParams }: Readonly<{ items: readonly TourStepListItem[]; textParams?: TourTextParams }>) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-1 text-xs" data-testid={setTourTestId("step-list")}>
      {items.map((item) => (
        <li key={item.labelKey} className="flex gap-2" data-testid={setTourTestId("step-list-item")}>
          {item.dotClassName && <span aria-hidden="true" className={`mt-1 size-2 shrink-0 rounded-full ${item.dotClassName}`} />}
          <span>
            <span className="text-foreground font-medium">{t(item.labelKey, textParams) as string}</span>
            {" — "}
            <span className="text-muted-foreground">{t(item.descriptionKey, textParams) as string}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function TourStepCard({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  isTargetEmpty = false,
  textParams,
  onNext,
  onPrev,
  onSkip,
}: Readonly<TourStepCardProps>) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDialogElement>(null);
  const descriptionId = useId();
  const [cardSize, setCardSize] = useState<SizeLike>(FALLBACK_CARD_SIZE);

  const [canAdvance, setCanAdvance] = useState(false);
  useEffect(() => {
    const check = () => setCanAdvance(!step.advanceWhen || document.querySelector(step.advanceWhen) !== null);
    check();
    if (!step.advanceWhen) return;
    const interval = setInterval(check, 100);
    return () => clearInterval(interval);
  }, [step.id, step.advanceWhen]);

  // Interactive steps put keyboard users at the control they are asked to use.
  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;

    setCardSize({ width: node.offsetWidth || FALLBACK_CARD_SIZE.width, height: node.offsetHeight || FALLBACK_CARD_SIZE.height });
    const target = step.interactive ? document.querySelector(step.target) : null;
    const focusableSelector =
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]';
    const control = target?.matches(focusableSelector) ? target : target?.querySelector(focusableSelector);
    if (!(control instanceof HTMLElement)) {
      node.focus({ preventScroll: true });
      return;
    }
    // Focus moves off the card, so tie its instructions to the control to have them announced
    const previousDescribedBy = control.getAttribute("aria-describedby");
    control.setAttribute("aria-describedby", [previousDescribedBy, descriptionId].filter(Boolean).join(" "));
    control.focus({ preventScroll: true });
    return () => {
      if (previousDescribedBy === null) control.removeAttribute("aria-describedby");
      else control.setAttribute("aria-describedby", previousDescribedBy);
    };
  }, [step.id, step.interactive, step.target, descriptionId]);

  const { top, left } = positionStepCard({
    targetRect,
    cardSize,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    preferred: step.placement,
  });
  const featureId = step.id.split(".")[0] ?? step.id;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;
  const showEmptyStateBody = isTargetEmpty && step.emptyState !== undefined;

  return (
    // A modal dialog's focus trap sits behind this portalled card; mounting a scope pauses it so Tab can reach Next/Back/Skip.
    // The card manages focus itself, so the scope must not move it on mount or restore it on unmount.
    <FocusScope asChild onMountAutoFocus={(event) => event.preventDefault()} onUnmountAutoFocus={(event) => event.preventDefault()}>
      <dialog
        ref={cardRef}
        // Non-modal on purpose: showModal() would make the rest of the page inert, including the field an interactive step points at.
        open
        aria-modal={step.interactive ? undefined : true}
        aria-label={t(step.titleKey, textParams) as string}
        tabIndex={-1}
        data-testid={setTourTestId("step-card")}
        className="bg-popover text-popover-foreground pointer-events-auto fixed z-[70] w-[min(90vw,24rem)] max-h-[85vh] space-y-3 overflow-y-auto rounded-lg border p-4 shadow-lg transition-all duration-300"
        style={{ top, left }}
        // Radix dialogs treat a document-level pointerdown as an outside press and close;
        // the card lives outside the dialog portal, so keep its clicks to itself
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <Badge variant="secondary" testId={setTourTestId("feature-badge")}>
            {t(`tour.features.${featureId}.title`) as string}
          </Badge>
          <Button variant="ghost" size="icon-sm" onClick={onSkip} aria-label={t("tour.controls.close")} testId={setTourTestId("close")}>
            <XIcon />
          </Button>
        </div>
        <div className="space-y-1" id={descriptionId}>
          <h2 className="text-base font-semibold" data-testid={setTourTestId("step-title")}>
            {t(step.titleKey, textParams) as string}
          </h2>
          <p className="text-muted-foreground text-sm" data-testid={setTourTestId("step-body")}>
            {t(showEmptyStateBody && step.emptyState ? step.emptyState.bodyKey : step.bodyKey, textParams) as string}
          </p>
          {step.list && step.list.length > 0 && <TourStepList items={step.list} textParams={textParams} />}
          {step.interactive && (
            <p className="text-xs font-medium" data-testid={setTourTestId("step-hint")}>
              {
                t(
                  step.hintKey ?? (step.advanceWhen ? "tour.controls.waitingForYou" : "tour.controls.continueWhenReady"),
                  textParams
                ) as string
              }
            </p>
          )}
        </div>
        <Progress value={((stepIndex + 1) / totalSteps) * 100} testId={setTourTestId("progress")} />
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground text-xs" data-testid={setTourTestId("step-count")}>
            {t("tour.controls.stepOf", { current: stepIndex + 1, total: totalSteps })}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onSkip} testId={setTourTestId("skip")}>
              {t("tour.controls.skip")}
            </Button>
            {!isFirstStep && (
              <Button variant="outline" size="sm" onClick={onPrev} testId={setTourTestId("back")}>
                {t("tour.controls.back")}
              </Button>
            )}
            <Button size="sm" disabled={Boolean(step.advanceWhen) && !canAdvance} onClick={onNext} testId={setTourTestId("next")}>
              {isLastStep ? t("tour.controls.done") : t("tour.controls.next")}
            </Button>
          </div>
        </div>
      </dialog>
    </FocusScope>
  );
}
